import React, { useEffect, useState } from 'react';
import { Search, RefreshCw, CheckCircle2, Copy, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import webhookLogService from '../../../../services/webhookLogService';
import '../users/UserManagement.css';

interface WebhookLog {
  id: number;
  source: string;
  event_id: string | null;
  status: 'success' | 'duplicate' | 'failed';
  http_status: number;
  error_message: string | null;
  project_id: number | null;
  created_at: string;
}

const WebhookLogList = () => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 1 });
  const [counts, setCounts] = useState({ success_count: 0, duplicate_count: 0, failed_count: 0 });
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedPayload, setExpandedPayload] = useState<Record<number, string>>({});
  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pagination.page_size,
        search: search || undefined,
        status: statusFilter || undefined,
      };
      const response = await webhookLogService.getAllLogs(params);
      const body = response.data || {};
      setLogs(body.data || []);
      setPagination({
        page: body.page || 1,
        page_size: body.page_size || 20,
        total: body.total || 0,
        total_pages: body.total_pages || 1,
      });
      setCounts({
        success_count: body.success_count || 0,
        duplicate_count: body.duplicate_count || 0,
        failed_count: body.failed_count || 0,
      });
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = async (log: WebhookLog) => {
    if (expandedId === log.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(log.id);
    if (!expandedPayload[log.id]) {
      try {
        setLoadingDetailId(log.id);
        const response = await webhookLogService.getLogById(log.id);
        const payload = response.data?.payload || '(no payload stored)';
        setExpandedPayload((prev) => ({ ...prev, [log.id]: payload }));
      } catch (error) {
        setExpandedPayload((prev) => ({ ...prev, [log.id]: 'Failed to load payload.' }));
      } finally {
        setLoadingDetailId(null);
      }
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const getStatusBadge = (status: WebhookLog['status']) => {
    if (status === 'success') return <span className="badge badge-success"><CheckCircle2 size={12} style={{ marginRight: 4 }} />Success</span>;
    if (status === 'duplicate') return <span className="badge badge-secondary"><Copy size={12} style={{ marginRight: 4 }} />Duplicate</span>;
    return <span className="badge badge-danger"><XCircle size={12} style={{ marginRight: 4 }} />Failed</span>;
  };

  return (
    <div className="user-management">
      <div className="page-header">
        <div className="header-left">
          <h1>Webhook Delivery Log</h1>
          <p>Every quote-paid webhook attempt from the DataCaptive quote tool, success or failure</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={() => fetchLogs(pagination.page)}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-content">
            <h3>Total Deliveries</h3>
            <div className="stat-value">{pagination.total}</div>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-content">
            <h3>Success</h3>
            <div className="stat-value">{counts.success_count}</div>
          </div>
        </div>
        <div className="stat-card internal">
          <div className="stat-content">
            <h3>Duplicate</h3>
            <div className="stat-value">{counts.duplicate_count}</div>
          </div>
        </div>
        <div className="stat-card dccp">
          <div className="stat-content">
            <h3>Failed</h3>
            <div className="stat-value">{counts.failed_count}</div>
          </div>
        </div>
      </div>

      <div className="controls-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by event ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
            onKeyPress={(e) => { if (e.key === 'Enter') fetchLogs(1); }}
          />
          <button className="btn btn-outline btn-sm" onClick={() => fetchLogs(1)}>Search</button>
        </div>

        <div className="controls-right">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setTimeout(() => fetchLogs(1), 0); }}
          >
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="duplicate">Duplicate</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading webhook logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <h3>No webhook deliveries found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Event ID</th>
                  <th>Project</th>
                  <th>HTTP</th>
                  <th>Error</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr>
                      <td data-label="Time">{formatDate(log.created_at)}</td>
                      <td data-label="Status">{getStatusBadge(log.status)}</td>
                      <td data-label="Event ID"><code>{log.event_id || '—'}</code></td>
                      <td data-label="Project">{log.project_id ? `#${log.project_id}` : '—'}</td>
                      <td data-label="HTTP">{log.http_status}</td>
                      <td data-label="Error">
                        {log.error_message ? (
                          <span className="text-muted" title={log.error_message}>
                            {log.error_message.length > 60 ? `${log.error_message.slice(0, 60)}…` : log.error_message}
                          </span>
                        ) : '—'}
                      </td>
                      <td data-label="Payload">
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => toggleExpand(log)}
                        >
                          {expandedId === log.id ? (
                            <>Hide <ChevronUp size={14} /></>
                          ) : (
                            <>Show <ChevronDown size={14} /></>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedId === log.id && (
                      <tr>
                        <td colSpan={7} style={{ background: '#f9fafb' }}>
                          {loadingDetailId === log.id ? (
                            <p style={{ margin: '8px 0' }}>Loading payload…</p>
                          ) : (
                            <pre style={{
                              margin: '8px 0', padding: '12px', background: '#111827', color: '#e5e7eb',
                              borderRadius: '8px', overflowX: 'auto', fontSize: '12px', whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>
                              {expandedPayload[log.id]}
                            </pre>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="pagination">
              <div style={{ display: 'flex' }}>
                <button
                  className="pagination-btn"
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.total_pages <= 5) pageNum = i + 1;
                    else if (pagination.page <= 3) pageNum = i + 1;
                    else if (pagination.page >= pagination.total_pages - 2) pageNum = pagination.total_pages - 4 + i;
                    else pageNum = pagination.page - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        className={`pagination-page ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => fetchLogs(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                >
                  Next
                </button>
              </div>
              <div className="pagination-info">
                Showing {(pagination.page - 1) * pagination.page_size + 1} to{' '}
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                {pagination.total} deliveries
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WebhookLogList;
